/**
 * MetaCreatives — tarjetas de anuncio con su imagen, texto, resultado, rankings
 * de calidad y enlace a la publicación real. Las usan la pestaña "Anuncios" del
 * análisis y el detalle de campaña.
 */
import React, { useMemo, useState } from 'react';
import { FaImage, FaPlay, FaExternalLinkAlt } from 'react-icons/fa';
import {
  compact, pct, label, resultLine, CTA_LABEL, FORMAT_LABEL, RANK_INFO, RANKS, STATUS_LABEL, statusTone,
} from './metaAdsShared';

const SORTS = [
  { id: 'spend', label: 'Mayor inversión', fn: (a, b) => (b.spend || 0) - (a.spend || 0) },
  // Sin resultado va al final: un costo "—" no es el mejor costo.
  { id: 'cpr', label: 'Mejor costo por resultado', fn: (a, b) => (a.result?.cost ?? Infinity) - (b.result?.cost ?? Infinity) },
  { id: 'ctr', label: 'Mayor CTR', fn: (a, b) => ((b.outboundCtr ?? b.ctr) || 0) - ((a.outboundCtr ?? a.ctr) || 0) },
];

export function CreativeGrid({ creatives, money, total, showCampaign = true }) {
  const [sortId, setSortId] = useState('spend');
  const sorted = useMemo(() => [...creatives].sort((SORTS.find((s) => s.id === sortId) || SORTS[0]).fn), [creatives, sortId]);
  const anyRank = creatives.some((c) => RANKS.some((r) => c[r.key] && RANK_INFO[c[r.key]]));
  return (
    <>
      <div className="mad-toolbar">
        <span className="mad-muted">
          {total && total > creatives.length ? `Los ${creatives.length} anuncios con mayor inversión de ${total}` : `${creatives.length} anuncios`}
        </span>
        <div className="mad-seg small">
          {SORTS.map((s) => (
            <button key={s.id} type="button" className={s.id === sortId ? 'active' : ''} onClick={() => setSortId(s.id)}>{s.label}</button>
          ))}
        </div>
      </div>
      <div className="mad-creatives">
        {sorted.map((c) => <CreativeCard key={c.id} c={c} money={money} showCampaign={showCampaign} />)}
      </div>
      {anyRank && (
        <p className="mad-foot">
          Los <b>rankings</b> comparan cada anuncio con los que compitieron por la misma audiencia.
          Meta solo los calcula a partir de ~500 impresiones.
        </p>
      )}
    </>
  );
}

export function CreativeCard({ c, money, showCampaign }) {
  const [imgOk, setImgOk] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const res = resultLine(c.result, money);
  const ranks = RANKS.filter((r) => c[r.key] && RANK_INFO[c[r.key]]);
  return (
    <article className="mad-creative">
      <div className="mad-creative-media">
        {c.thumbnail && imgOk
          ? <img src={c.thumbnail} alt="" loading="lazy" onError={() => setImgOk(false)} />
          : <span className="mad-ph"><FaImage /></span>}
        {c.isVideo && <span className="mad-play"><FaPlay /></span>}
        <div className="mad-creative-badges">
          {c.format && <span>{label(FORMAT_LABEL, c.format)}</span>}
          {c.status && c.status !== 'ACTIVE' && <span className={`tone-${statusTone(c.status)}`}>{label(STATUS_LABEL, c.status)}</span>}
        </div>
      </div>
      <div className="mad-creative-body">
        <h4 title={c.name}>{c.title || c.name}</h4>
        {showCampaign && <p className="mad-creative-sub" title={c.campaign}>{c.campaign}{c.adset ? ` · ${c.adset}` : ''}</p>}
        {c.body && (
          <p className={`mad-creative-text ${expanded ? 'open' : ''}`} onClick={() => setExpanded((e) => !e)} title={expanded ? 'Ver menos' : 'Ver texto completo'}>
            {c.body}
          </p>
        )}
        <div className="mad-creative-kpis">
          <span><b>{money(c.spend)}</b>inversión</span>
          {res
            ? <span><b>{res.value}</b>{res.label}{res.cost && <em>{res.cost}</em>}</span>
            : <span><b>{compact(c.impressions)}</b>impresiones</span>}
          <span><b>{pct(c.outboundCtr ?? c.ctr)}</b>{c.outboundCtr != null ? 'CTR de salida' : 'CTR'}</span>
          <span><b>{money.unit(c.cpm)}</b>CPM</span>
        </div>
        {ranks.length > 0 && (
          <div className="mad-ranks">
            {ranks.map((r) => (
              <span key={r.key} className={`mad-rank tone-${RANK_INFO[c[r.key]].tone}`} title={`${r.label}: ${RANK_INFO[c[r.key]].label}`}>
                {r.label}: {RANK_INFO[c[r.key]].label.split(' (')[0]}
              </span>
            ))}
          </div>
        )}
        <div className="mad-creative-foot">
          {c.cta && <span className="mad-cta">{label(CTA_LABEL, c.cta)}</span>}
          {c.link && <a href={c.link} target="_blank" rel="noreferrer">Ver anuncio <FaExternalLinkAlt /></a>}
        </div>
      </div>
    </article>
  );
}
